import { AbuseCase } from '../models/abuse-case.model';

type CasePermissionFields = Pick<
  AbuseCase,
  | 'status'
  | 'displayStatus'
  | 'isClosed'
  | 'isCustomAbuseType'
  | 'analystId'
  | 'analystName'
  | 'externalAnalystId'
  | 'externalAnalystName'
>;

type CurrentUser = {
  isRoot: boolean;
  id: string;
  username?: string;
  email?: string;
};

type CasePermissions = {
  isAssigned: boolean;
  canModifyCase: boolean;
  canEditNotesOrAttachments: boolean;
  canAssignAnalyst: boolean;
};

type AuthForCasePermissions = {
  isRoot: () => boolean;
  getCurrentUser: () => { id?: string; userId?: string; email?: string; username?: string; name?: string } | null;
  getToken: () => string | null;
  decodeAccessTokenPayload: (rawToken: string | null) => Record<string, unknown> | null;
};

function normalize(value?: string | null): string {
  return (value ?? '').trim().toLowerCase();
}

function isUserAssignedToCase(caseItem: CasePermissionFields, user: CurrentUser): boolean {
  const isRoot = user.isRoot === true;

  const assignedId = normalize(isRoot ? caseItem.analystId : caseItem.externalAnalystId);
  const assignedName = normalize(isRoot ? caseItem.analystName : caseItem.externalAnalystName);

  if (!assignedId && !assignedName) return false;

  const userId = normalize(user.id);
  const username = normalize(user.username);
  const email = normalize(user.email);
  const emailPrefix = email.includes('@') ? email.split('@')[0] : '';

  if (assignedId && userId && assignedId === userId) return true;
  if (assignedName && username && assignedName === username) return true;
  if (assignedName && emailPrefix && assignedName === emailPrefix) return true;
  if (assignedName && emailPrefix.includes('.') && assignedName === emailPrefix.split('.')[0]) return true;

  return false;
}

function isAnalystAssignedToCase(caseItem: CasePermissionFields, isRoot: boolean): boolean {
  if (isRoot) {
    return !!(normalize(caseItem.analystId) || normalize(caseItem.analystName));
  }
  return !!(normalize(caseItem.externalAnalystId) || normalize(caseItem.externalAnalystName));
}

function canModifyCase(caseItem: CasePermissionFields, user: CurrentUser): boolean {
  return isUserAssignedToCase(caseItem, user);
}

function canEditNotesOrAttachments(caseItem: CasePermissionFields, user: CurrentUser): boolean {
  if (caseItem.isClosed) return false;
  if (normalize(caseItem.status) === 'classifying') return false;
  return isUserAssignedToCase(caseItem, user);
}

function canAssignAnalyst(caseItem: CasePermissionFields, user: CurrentUser): boolean {
  if (caseItem.isClosed) return false;

  const status = (caseItem.status ?? '').trim();

  if (user.isRoot && caseItem.isCustomAbuseType) return false;
  if (status === 'Escalate') return !user.isRoot;
  if (user.isRoot && status === 'Under Investigation') return true;

  return false;
}

function buildCurrentUserFromAuth(auth: AuthForCasePermissions): CurrentUser | null {
  const stored = auth.getCurrentUser();
  const payload = auth.decodeAccessTokenPayload(auth.getToken());
  const id = String(stored?.id ?? stored?.userId ?? payload?.['sub'] ?? '').trim();
  if (!id && !stored?.email && !payload?.['email']) {
    return null;
  }

  const email = String(stored?.email ?? payload?.['email'] ?? '').trim();
  let username = String(
    stored?.username ??
    payload?.['username'] ??
    payload?.['user'] ??
    stored?.name ??
    payload?.['name'] ??
    ''
  ).trim();

  if (!username && email) {
    const emailUser = email.split('@')[0] ?? '';
    username = emailUser.includes('.') ? emailUser.split('.')[0] : emailUser;
  }

  return {
    isRoot: auth.isRoot(),
    id,
    username: username || undefined,
    email: email || undefined,
  };
}

function getCasePermissions(
  caseItem: CasePermissionFields | null | undefined,
  user: CurrentUser | null,
): CasePermissions {
  if (!caseItem || !user) {
    return {
      isAssigned: false,
      canModifyCase: false,
      canEditNotesOrAttachments: false,
      canAssignAnalyst: false,
    };
  }

  const isAssigned = isUserAssignedToCase(caseItem, user);

  return {
    isAssigned,
    canModifyCase: isAssigned,
    canEditNotesOrAttachments: canEditNotesOrAttachments(caseItem, user),
    canAssignAnalyst: canAssignAnalyst(caseItem, user),
  };
}

const CASE_MODIFICATION_DENIED_MESSAGE = 'You do not have permission to modify this case';
const CASE_NOTES_DENIED_MESSAGE = 'You do not have permission to modify notes or attachments';

function isCasePermissionDeniedError(message: unknown): boolean {
  if (typeof message !== 'string') return false;
  const normalized = message.toLowerCase();
  return (
    normalized.includes('permission to modify this case') ||
    normalized.includes('permission to modify notes or attachments') ||
    normalized.includes('must be assigned as the analyst')
  );
}

export {
  type CasePermissionFields,
  type CurrentUser,
  type CasePermissions,
  type AuthForCasePermissions,
  normalize,
  isUserAssignedToCase,
  isAnalystAssignedToCase,
  canModifyCase,
  canEditNotesOrAttachments,
  canAssignAnalyst,
  buildCurrentUserFromAuth,
  getCasePermissions,
  CASE_MODIFICATION_DENIED_MESSAGE,
  CASE_NOTES_DENIED_MESSAGE,
  isCasePermissionDeniedError,
};
